import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Howl } from 'howler';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { SitesService } from 'src/app/sites/sites.service';
import { UsersService } from 'src/app/users/users.service';
import * as _ from 'underscore';
import { Router } from '@angular/router';

@Component({
	selector: 'app-detailed-ranking-page',
	templateUrl: './detailed-ranking-page.component.html',
	styleUrls: ['./detailed-ranking-page.component.scss']
})
export class DetailedRankingPageComponent implements OnInit, AfterViewInit, OnDestroy{
	@Input() siteId = 0;
	groups = {
		ready: [],
		cooled_down: [],
		cooling_down: [],
		charging: [],
		malfunctioned: [],
		not_connected: [],
		unknown: []
 	};
	filteredGroups = {
		ready: [],
		cooled_down: [],
		cooling_down: [],
		charging: [],
		malfunctioned: [],
		not_connected: [],
		unknown: []
 	};
	hasDataToShow: boolean = false;

	data = [];
	dataSorted = [];
	timeoutId;

	// buzzer sound
	buzzing: boolean = false;
	sound: Howl;
	buzzerEnabled: boolean = true;
	getChargLinkGroupsData: any;

	refreshTimerId;
	failedDataFetchCounter: number = 0;
	getRankEnables: boolean = true;
	siteSub: Subscription = new Subscription();

	batteryStatus: {[key: string]: {value: string, image: string, color: string}} = {
		"ready": {
			value: this.translateService.instant('charglink_brm.ready_to_pick'),
			image: "fully_charged.png",
			color: "#3B963E",
		},
		"cooled_down": {
			value: this.translateService.instant('charglink_brm.cooled_down'),
			image: "fully_charged.png",
			color: "#3B963E",
		},
		"cooling_down": {
			value: this.translateService.instant('charglink_brm.cooling_down'),
			image: "cooling_down.png",
			color: '#00AED8',
		},
		"charging": {
			value: this.translateService.instant('charglink_brm.charging'),
			image: "charging.png",
			color: '#FC8916',
		},
		"malfunctioned": {
			value: this.translateService.instant('charglink_brm.potentially_malfunctioned'),
			image: "potentially_malfunctioned.png",
			color: '#FF0000',
		},
		"not_connected": {
			value: this.translateService.instant('charglink_brm.battery_not_connect'),
			image: "charging.png",
			color: '#FF0000',
		},
		"unknown": {
			value: this.translateService.instant('charglink_brm.unknown_status'),
			image: "warning.png",
			color: '#8A8A8A',
		}
	};

	showFilter: boolean = false;
	filter = {
		groupname: 'all',
		status: 'all',
		showNotConnectedChargers: false,
		showUnknownChargers: false,
	}
	toShowStatuses = ['ready', 'cooled_down', 'cooling_down', 'charging', 'malfunctioned'];
	allStatuses = ['ready', 'cooled_down', 'cooling_down', 'charging', 'malfunctioned'];

	statusesList = [
		{id: 'all', name: this.translateService.instant('g.all')},
		{id: 'ready', name: this.translateService.instant('charglink_brm.ready_to_pick')},
		{id: 'cooled_down', name: this.translateService.instant('charglink_brm.cooled_down')},
		{id: 'cooling_down', name: this.translateService.instant('charglink_brm.cooling_down')},
		{id: 'charging', name: this.translateService.instant('charglink_brm.charging')},
		{id: 'malfunctioned', name: this.translateService.instant('charglink_brm.potentially_malfunctioned')},
	];

	groupsList = [
		{id: 'all', name: this.translateService.instant('g.all')},
	];

	readonly ALLOWED_MAINTENANCE_STATUSES = ['charging', 'cooling_down', 'cooled_down'];

	@ViewChild('maintenanceConfirmModal') maintenanceConfirmModal;
	// the row awaiting confirmation; untyped because the rows built in prepareGroupsData() have no shared interface
	deviceToToggleMaintenance: any = null;
	maintenanceConfirmTitleKey = 'charglink_brm.move_to_maintenance_title';
	maintenanceConfirmMessageKey = 'charglink_brm.move_to_maintenance_confirm_msg';
	maintenanceModalSub: Subscription = new Subscription();

	constructor(
		private sitesService: SitesService,
		private cdref: ChangeDetectorRef,
		public usersService: UsersService,
		private router: Router,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
		private translateService: TranslateService,
	) {}

	ngOnInit() {
		if (!this.usersService.hasAccessFunction('show_charglink_brm'))
			return this.router.navigate(['/unauthorized']);

		this.siteSub = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (!siteInfo || !siteInfo['id'])
				return;
			this.commonService.setPageTabTitle(`${siteInfo.customer_name} - ${siteInfo.name} ${this.translateService.instant('nav.detailed_ranking_page')}`);
		});

		this.initSound();
		this.getChargLinkGroups(false);

		this.refreshTimerId = setInterval(() => {
			window.location.reload();
		}, 15*60*1000);
	}

	ngAfterViewInit() {
		this.maintenanceModalSub = this.maintenanceConfirmModal.onClose.subscribe((confirmed: boolean) => {
			const device = this.deviceToToggleMaintenance;
			this.deviceToToggleMaintenance = null;

			if (confirmed && device)
				this.toggleMaintenance(device);
		});
	}

	getChargLinkGroups(noUiBlock=true) {
		let checkForUpdate = true;

		const filters = {
			siteId: this.siteId,
			fromDate: '',
			toDate: '',
			isHistory: false,
			publicPageReq: false
		};

		this.sitesService.getBRMGroupsData(filters, noUiBlock).subscribe((data: any) => {
			if (this.failedDataFetchCounter) {
				this.failedDataFetchCounter = 0;
				this.setPageAsConnectivityStatus(true);
			}

			let groups = data.groups;

			// Remove empty groups
			groups = _.filter(groups, function(group){ return group['groupname'];})

			if (!groups.length)
				return this.repeatGetChargLinkGroups();

			for (let group of groups) {
				group['groupname'] = group['groupname'].toUpperCase();
				let {thisGroup, checkUpdate} = this.getGroupDataById(group.id, data, checkForUpdate);
				checkForUpdate = checkUpdate;
				let statuses = group.innergroups;

				for (let status in statuses) {
					let thisGroupStatusChargersById = {};
					let oldGroupIds = [];
					let newGroupIds = [];

					let groupStatuses = group['innergroups'][status];
					let oldGroupStatuses = thisGroup?.['innergroups']?.[status] || [];

					if (checkForUpdate) {
						oldGroupIds = _.pluck(oldGroupStatuses, 'id');
						newGroupIds = _.pluck(groupStatuses, "id");

						checkForUpdate = this.updateDomIfFindChange(oldGroupIds, newGroupIds, data);
					}

					thisGroupStatusChargersById = _.indexBy(oldGroupStatuses, 'id');
					for (let charger of groupStatuses) {
						if (checkForUpdate)
							this.updateChargesData(charger, thisGroupStatusChargersById, data);

						if (this.buzzerEnabled)
							this.checkAndPlayBuzzer(charger);
					}
				}
				this.dataSorted = _.sortBy(this.data, ['is_full_row', 'innergroups[\'ready\']']).reverse();
			}
			this.prepareGroupsData();

			this.repeatGetChargLinkGroups();
		}, () => {
			this.failedDataFetchCounter++;
			if (this.failedDataFetchCounter == 30)
				this.setPageAsConnectivityStatus(false);

			this.repeatGetChargLinkGroups();
		});
	}

	setPageAsConnectivityStatus(isOnline: boolean) {
		if (!isOnline) {
			clearInterval(this.refreshTimerId)
			return;
		}

		if (!this.refreshTimerId) {
			this.refreshTimerId = setInterval(() => {
				window.location.reload();
			}, 15*60*1000);
		}
	}

	initSound() {
		let pc = this;

		this.sound = new Howl({
			src: ['/sctportal/audio/buzz.mp3'],
			format: ['mp3'],
			autoUnlock: true,
			loop: true,

			onplayerror: function() {
				this.sound.once('unlock', function() {
					pc.playBuzzer();
				});
			}
		});
	}

	playBuzzer() {
		if (this.buzzing)
			return;

		let buzzerTime = 16000;
		this.buzzing = true;
		this.sound.play();

		this.timeoutId = setTimeout(() => {
			this.buzzing = false;
			this.sound.stop();
		}, buzzerTime);
	}

	toggleBuzzer() {
		this.buzzerEnabled = !this.buzzerEnabled;

		if (this.buzzerEnabled)
			this.sitesService.enableBuzzer();
		else
			this.sound.stop();
	}

	checkAndPlayBuzzer(charger) {
		if (!this.buzzing && charger.alertFlag && !charger.inMaintenance)
			this.playBuzzer();
	}

	repeatGetChargLinkGroups() {
		this.getChargLinkGroupsData = setTimeout(() => {
			if(this.getRankEnables)
				this.getChargLinkGroups();
		}, 10000);
	}

	getGroupDataById(id: number, data: any, checkForUpdate: boolean) {
		let thisGroup = {};

		for (let dataGroup of this.data) {
			if (dataGroup.id == id) {
				thisGroup = dataGroup;
				break;
			}
		}

		if (!thisGroup['id']) {
			this.data = data['groups'];
			checkForUpdate = false;
		}

		return {thisGroup, checkUpdate: checkForUpdate};
	}

	updateDomIfFindChange(oldIds, newIds, data) {
		let checkForUpdate = true;

		// Both directions matter: a battery being added to the ready list is as much a change as
		// one being removed, and a group can now hold two of them (ST-2328).
		const areEquals = _.isEmpty(_.difference(oldIds, newIds)) && _.isEmpty(_.difference(newIds, oldIds));
		if (!areEquals) {
			this.data = data['groups'];
			checkForUpdate = false;
			this.cdref.detectChanges();
		}

		return checkForUpdate;
	}

	updateChargesData(charger, thisGroupStatusChargersById, data) {
		for (let key in charger) {
			// update data if there is change in chargers list except for these attributes: ['relaxedBatteryTimer', 'time'];
			if (!['relaxedBatteryTimer', 'time'].includes(key)) {
				if (!thisGroupStatusChargersById[charger.id] || thisGroupStatusChargersById[charger.id][key] != charger[key]) {
					this.data = data.groups;
					break;
				}
			}
		}
	}

	prepareGroupsData() {
		const result = {
			ready: [],
			cooled_down: [],
			cooling_down: [],
			charging: [],
			malfunctioned: [],
			not_connected: [],
			unknown: []
		}

		for (const data of this.dataSorted) {
			if (!data.groupname)
				continue;

			const readyGroups = data.innergroups.ready || [];
			const relaxedGroups = data.innergroups.relaxed || [];
			const chargingGroups = data.innergroups.charging || [];
			const relaxingGroups = data.innergroups.relaxing || [];
			const potentiallyMalfunctionedGroups = data.innergroups.malfunctioned || [];
			const notConnectedGroups = data.innergroups.not_connected || [];
			const unknownGroups = data.innergroups.unknown || [];

			const coolingDownGroups = [...relaxingGroups];
			const cooledDownGroups = [...relaxedGroups];

			const groupMappings = [
				{ groups: cooledDownGroups, status: 'cooled_down' },
				{ groups: readyGroups, status: 'ready' },
				{ groups: coolingDownGroups, status: 'cooling_down' },
				{ groups: chargingGroups, status: 'charging' },
				{ groups: potentiallyMalfunctionedGroups, status: 'malfunctioned' },
				{ groups: notConnectedGroups, status: 'not_connected' },
				{ groups: unknownGroups, status: 'unknown' }
			];

			for (const { groups, status } of groupMappings) {
				for (const group of groups) {
					result[status].push({
						name: data.groupname,
						batteryStatus: status,
						inMaintenance: group.inMaintenance,
						connected: group.connected,
						mac_address: group.id,
						location: group.batteryid,
					});
				}
			}
		}

		this.hasDataToShow = Object.values(result).some(arr => arr.length > 0);
		this.groups = result;
		this.prepareGroupsList();
		this.applyFilter();
	}

	prepareGroupsList() {
		this.groupsList = [{id: 'all', name: this.translateService.instant('g.all')}];
		const groupNames = new Set();
		for (const status in this.groups) {
			for (const group of this.groups[status]) {
				if (group.name && !groupNames.has(group.name)) {
					this.groupsList.push({id: group.name, name: group.name});
					groupNames.add(group.name);
				}
			}
		}
		this.groupsList.sort((a, b) => a.name.localeCompare(b.name));
	}

	applyFilter() {
		this.filteredGroups = {
			ready: this.groups.ready.filter(group => this.filterGroup(group)),
			cooled_down: this.groups.cooled_down.filter(group => this.filterGroup(group)),
			cooling_down: this.groups.cooling_down.filter(group => this.filterGroup(group)),
			charging: this.groups.charging.filter(group => this.filterGroup(group)),
			malfunctioned: this.groups.malfunctioned.filter(group => this.filterGroup(group)),
			not_connected: this.groups.not_connected.filter(group => this.filterGroup(group)),
			unknown: this.groups.unknown.filter(group => this.filterGroup(group)),
		};
	}

	filterGroup(group) {
		const filterName = this.filter.groupname && this.filter.groupname != 'all' ? this.filter.groupname.trim().toLowerCase() : '';
		const groupName = group.name ? group.name.trim().toLowerCase() : '';

		if (filterName && !groupName.includes(filterName))
			return false;

		return true;
	}

	ngOnDestroy() {
		if (this.sound)
			this.sound.stop();

		if(this.timeoutId)
			clearTimeout(this.timeoutId);

		if(this.getChargLinkGroupsData)
			clearTimeout(this.getChargLinkGroupsData);

		this.getRankEnables = false;
		if (this.refreshTimerId)
			clearInterval(this.refreshTimerId);

		this.maintenanceModalSub.unsubscribe();
	}

	onStatusChange(status: string) {
		if (status === 'all')
			this.toShowStatuses = [...this.allStatuses];
		else
			this.toShowStatuses = [status];


		if (this.filter.showNotConnectedChargers && !this.toShowStatuses.includes('not_connected'))
			this.toShowStatuses.push('not_connected');

		if (this.filter.showUnknownChargers && !this.toShowStatuses.includes('unknown'))
			this.toShowStatuses.push('unknown');
	}

	toggleIdleChargers() {
		const index = this.toShowStatuses.indexOf('not_connected');

		if (this.filter.showNotConnectedChargers && index === -1)
			return this.toShowStatuses.push('not_connected');

		if (!this.filter.showNotConnectedChargers && index > -1)
			return this.toShowStatuses.splice(index, 1);

		return;
	}

	toggleUnknownChargers() {
		const index = this.toShowStatuses.indexOf('unknown');

		if (this.filter.showUnknownChargers && index === -1)
			return this.toShowStatuses.push('unknown');

		if (!this.filter.showUnknownChargers && index > -1)
			return this.toShowStatuses.splice(index, 1);

		return;
	}

	// device is `any` to match toggleMaintenance() below — see deviceToToggleMaintenance
	confirmToggleMaintenance(device: any) {
		this.deviceToToggleMaintenance = device;

		const isExitingMaintenance = !!device.inMaintenance;
		this.maintenanceConfirmTitleKey = isExitingMaintenance ?
			'charglink_brm.exit_maintenance_title' : 'charglink_brm.move_to_maintenance_title';
		this.maintenanceConfirmMessageKey = isExitingMaintenance ?
			'charglink_brm.exit_maintenance_confirm_msg' : 'charglink_brm.move_to_maintenance_confirm_msg';

		this.maintenanceConfirmModal.show();
	}

	toggleMaintenance(device: any) {
		const newStatus = !device.inMaintenance;

		this.sitesService.setBRMDeviceMaintenance(this.siteId, device.mac_address, newStatus).subscribe((res: any) => {
			if (res?.success) {
				device.inMaintenance = newStatus;
				clearTimeout(this.getChargLinkGroupsData);
				this.getChargLinkGroups(false);
			}
		});
	}
}
